import React, { useEffect, useState } from 'react';
import { useUmami, UmamiEvents } from './hooks/useUmami';
import { domainUrl, ROUTES } from './domainConfig';
import { DADA, TornLabel, DadaBackdrop, DadaStyles } from './components/dada';
import { useTranslation } from './i18n';

export default function WerepresenttalentPage({ onNavigate }) {
  const { t } = useTranslation();
  const [appointmentForm, setAppointmentForm] = useState({
    name: '',
    email: '',
    company_name: '',
    phone: '',
    message: ''
  });
  const [appointmentBusy, setAppointmentBusy] = useState(false);
  const [appointmentSent, setAppointmentSent] = useState(false);
  const [appointmentError, setAppointmentError] = useState('');
  const { trackEvent } = useUmami();

  useEffect(() => {
    const previousTitle = document.title;
    document.title = t('route_titles.bedrijven');
    return () => {
      document.title = previousTitle;
    };
  }, [t]);

  const COMPANY_API = process.env.REACT_APP_COMPANY_API || 'http://127.0.0.1:8001';

  const handleCTAClick = (path, location) => {
    trackEvent(UmamiEvents.CTA_CLICKED, { button: path, location });
    onNavigate(path);
  };

  const scrollToAppointmentForm = () => {
    document.getElementById('afspraak-formulier')?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToHowItWorks = () => {
    document.getElementById('hoe-het-werkt')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleAppointmentSubmit = async (event) => {
    event.preventDefault();
    setAppointmentError('');

    if (!appointmentForm.name.trim() || !appointmentForm.email.trim() || !appointmentForm.company_name.trim()) {
      setAppointmentError(t('company_marketing.form.required_error'));
      return;
    }

    setAppointmentBusy(true);
    try {
      const response = await fetch(`${COMPANY_API}/api/company/appointment-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: appointmentForm.name.trim(),
          email: appointmentForm.email.trim(),
          company_name: appointmentForm.company_name.trim(),
          phone: appointmentForm.phone.trim(),
          message: appointmentForm.message.trim()
        })
      });

      const data = await response.json();
      if (response.ok && data.ok) {
        setAppointmentSent(true);
      } else {
        setAppointmentError(data.detail || t('company_marketing.form.submit_failed'));
      }
    } catch (error) {
      setAppointmentError(t('company_marketing.form.submit_error'));
    }
    setAppointmentBusy(false);
  };

  const organisatieUrl = ROUTES.bedrijvenPortaal;
  const kandidaatUrl = domainUrl('kandidaat', '/');
  const werepresentUrl = domainUrl('werepresent', ROUTES.werepresentLanding);
  const privacyUrl = domainUrl('werepresent', ROUTES.privacy);
  const voorwaardenUrl = domainUrl('werepresent', ROUTES.voorwaarden);
  const aiPolicyUrl = domainUrl('werepresent', ROUTES.aiPolicy);
  const cookieUrl = domainUrl('werepresent', ROUTES.cookieverklaring);

  const pillars = [
    t('company_marketing.pillars.identity'),
    t('company_marketing.pillars.ambition'),
    t('company_marketing.pillars.skills'),
    t('company_marketing.pillars.conditions'),
  ];
  const oldWay = [
    t('company_marketing.old_way.item_1'),
    t('company_marketing.old_way.item_2'),
    t('company_marketing.old_way.item_3'),
  ];
  const proofPoints = [
    t('company_marketing.proof_points.item_1'),
    t('company_marketing.proof_points.item_2'),
    t('company_marketing.proof_points.item_3'),
  ];
  const reasons = [
    t('company_marketing.reasons.item_1'),
    t('company_marketing.reasons.item_2'),
    t('company_marketing.reasons.item_3'),
    t('company_marketing.reasons.item_4'),
    t('company_marketing.reasons.item_5'),
  ];

  const STEPS = [
    ['01', t('company_marketing.steps.step_1.title'), t('company_marketing.steps.step_1.body')],
    ['02', t('company_marketing.steps.step_2.title'), t('company_marketing.steps.step_2.body')],
    ['03', t('company_marketing.steps.step_3.title'), t('company_marketing.steps.step_3.body')],
    ['04', t('company_marketing.steps.step_4.title'), t('company_marketing.steps.step_4.body')],
    ['05', t('company_marketing.steps.step_5.title'), t('company_marketing.steps.step_5.body')],
    ['06', t('company_marketing.steps.step_6.title'), t('company_marketing.steps.step_6.body')]
  ];

  return (
    <main className="relative min-h-screen overflow-hidden bg-[#f6f1e8] text-[#151827]">
      <DadaStyles />
      <DadaBackdrop />

      {/* HEADER */}
      <header className="relative z-20 mx-auto flex w-full max-w-7xl items-center justify-between px-5 py-6 sm:px-8">
        <button
          onClick={() => onNavigate('/')}
          className="flex items-center gap-3 text-left"
          aria-label="Naar startpagina"
        >
          <img src="/logozondertekst.png" alt="werepresenttalent" className="h-9 w-auto shrink-0 sm:h-10" />
          <div className="leading-none">
            <div className="text-lg font-black tracking-tight text-[#151827] md:text-xl">werepresenttalent.nl</div>
            <div className="mt-1 text-[10px] uppercase tracking-[0.18em] text-[#151827]/60 md:text-xs">{t('company_marketing.tagline')}</div>
          </div>
        </button>
        <nav className="hidden items-center gap-7 text-sm font-bold uppercase tracking-wider text-[#151827]/75 md:flex">
          <button onClick={scrollToHowItWorks}>{t('company_marketing.nav.how_it_works')}</button>
          <button onClick={() => document.getElementById('investering')?.scrollIntoView({ behavior: 'smooth' })}>{t('company_marketing.hundred_hours_short')}</button>
          <button onClick={scrollToAppointmentForm}>{t('company_marketing.cta.make_appointment')}</button>
        </nav>
        <button
          onClick={() => handleCTAClick(organisatieUrl, 'header-primary')}
          className="rounded-full bg-[#151827] px-5 py-3 text-sm font-black text-white shadow-xl transition hover:-translate-y-0.5 sm:px-6"
        >
          {t('company_marketing.cta.start_org')}
        </button>
      </header>

      {/* HERO */}
      <section className="relative z-10 mx-auto grid max-w-7xl grid-cols-1 items-center gap-10 px-5 pb-16 pt-6 sm:px-8 lg:grid-cols-[1.05fr_.95fr] lg:pb-20 lg:pt-10">
        <div className="animate-dada-in">
          <div className="mb-7 flex flex-wrap gap-3">
            <TornLabel className="-rotate-2">{t('company_marketing.hero_kicker')}</TornLabel>
            <TornLabel className="rotate-1" bgColor={DADA.purple} textColor="#ffffff">{t('company_marketing.hero_side.line_1')}</TornLabel>
            <TornLabel className="-rotate-1" bgColor={DADA.green} textColor="#ffffff">{t('company_marketing.hero_side.line_2')}</TornLabel>
          </div>

          <h1 className="relative max-w-5xl text-[clamp(2.6rem,8.5vw,7.4rem)] font-black leading-[0.9] tracking-[-0.04em]">
            {t('company_marketing.hero_title_prefix')} <span className="inline-block text-[#713d8d]">{t('company_marketing.hundred_hours_short')}</span> {t('company_marketing.hero_title_suffix')}
            {/* <span className="absolute -right-2 top-4 hidden rotate-12 rounded-full border-4 border-[#151827] px-4 py-2 text-base tracking-normal lg:block">
              4 pijlers
            </span> */}
          </h1>

          <p className="mt-7 max-w-2xl text-xl font-semibold leading-snug text-[#2a2f40] sm:text-2xl">
            {t('company_marketing.hero_subtitle')}
          </p>

          <div className="mt-10 flex flex-col gap-4 sm:flex-row">
            <button
              onClick={() => handleCTAClick(organisatieUrl, 'hero-primary')}
              className="group rounded-2xl bg-[#713d8d] px-8 py-5 text-left text-xl font-black text-white transition hover:translate-x-1 hover:translate-y-1"
              style={{ boxShadow: '10px 10px 0 #151827' }}
            >
              {t('company_marketing.cta.start_org_arrow')}
            </button>
            <button
              onClick={scrollToAppointmentForm}
              className="rounded-2xl border-4 border-[#151827] bg-white px-8 py-5 text-xl font-black transition hover:translate-x-1 hover:translate-y-1"
              style={{ boxShadow: '8px 8px 0 #3f7f65' }}
            >
              {t('company_marketing.cta.make_appointment')}
            </button>
            <button
              onClick={scrollToHowItWorks}
              className="rounded-2xl border-4 border-[#151827] bg-white px-8 py-5 text-xl font-black transition hover:translate-x-1 hover:translate-y-1"
              style={{ boxShadow: '8px 8px 0 #713d8d' }}
            >
              {t('company_marketing.cta.how_it_works')}
            </button>
          </div>
        </div>

        <div className="relative animate-dada-in-delayed">
          <div className="absolute -left-8 top-16 z-0 h-32 w-32 rounded-full bg-[#3f7f65] opacity-80 blur-sm" />
          <div className="absolute -right-4 bottom-20 z-0 h-40 w-40 rounded-full bg-[#713d8d] opacity-70 blur-sm" />

          <div
            className="relative z-10 rotate-1 rounded-[2rem] border-4 border-[#151827] bg-white p-6"
            style={{ boxShadow: '18px 18px 0 #151827' }}
          >
            <div className="mb-5 flex items-center justify-between">
              <div className="rounded-full bg-[#151827] px-4 py-2 text-xs font-black uppercase tracking-widest text-white sm:text-sm">{t('company_marketing.promise_label')}</div>
              <span className="text-2xl">✦</span>
            </div>

            <div className="grid gap-4">
              <div className="rounded-3xl border-2 border-[#151827]/15 bg-[#f6f1e8] p-5">
                <div className="mb-2 text-lg font-black">{t('company_marketing.promise_body')}</div>
                <div className="h-3 overflow-hidden rounded-full bg-[#151827]/10">
                  <div className="h-full w-[78%] rounded-full bg-gradient-to-r from-[#3f7f65] to-[#713d8d]" />
                </div>
              </div>
              <div className="rounded-3xl bg-[#151827] p-5 text-white">
                <div className="mb-1 text-2xl font-black">{t('company_marketing.hero_card.title')}</div>
                <p className="text-white/75">{t('company_marketing.hero_card.body')}</p>
              </div>
              {/* <div className="-rotate-2 rounded-3xl border-2 border-[#151827] bg-[#713d8d] p-5 text-white">
                <div className="text-xl font-black">Stop met gokken, begin met zeker weten.</div>
              </div> */}
              <div className="flex items-center gap-4 rounded-3xl border-2 border-dashed border-[#151827] bg-white p-5">
                <div className="text-3xl text-[#3f7f65]">⌁</div>
                <div>
                  <div className="font-black uppercase">Werepresenttalent</div>
                  <div className="text-sm text-[#151827]/60">{t('company_marketing.tagline')}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="relative z-10 overflow-hidden border-y-4 border-[#151827] bg-[#151827] py-5 text-white">
        <div className="whitespace-nowrap text-2xl font-black uppercase tracking-tight sm:text-3xl animate-marquee">
          {t('company_marketing.marquee')}
        </div>
      </section>

      {/* PROBLEEM */}
      <section className="relative z-10 bg-[#151827] py-20 text-white md:py-28">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <TornLabel className="-rotate-2" bgColor={DADA.green} textColor="#ffffff">{t('company_marketing.section_recruitment_certainty')}</TornLabel>
          <h2 className="mb-12 mt-6 text-[clamp(1.9rem,5vw,3.4rem)] font-black leading-[0.95] tracking-[-0.03em]">
            {t('company_marketing.problem_title_prefix')} <span className="text-white">{t('company_marketing.problem_title_accent')}</span>
          </h2>

          <div className="mx-auto mb-10 max-w-md">
            <div
              className="rotate-1 rounded-[1.75rem] border-4 border-white/40 bg-white/5 p-6 text-left"
              style={{ boxShadow: '8px 8px 0 #3f7f65' }}
            >
              <h3 className="mb-4 text-base font-black uppercase tracking-wider text-[#8fd6b6]">{t('company_marketing.old_way_title')}</h3>
              <ul className="space-y-2 text-lg text-gray-300">
                {oldWay.map((item) => <li key={item}>- {item}</li>)}
              </ul>
            </div>
          </div>

          <div className="mx-auto max-w-md">
            <div
              className="-rotate-1 rounded-[1.75rem] border-4 border-white/40 bg-white/10 p-6 text-left"
              style={{ boxShadow: '8px 8px 0 #713d8d' }}
            >
              <h3 className="mb-4 text-base font-black uppercase tracking-wider text-white">{t('company_marketing.not_based_on_title')}</h3>
              <ul className="space-y-2 text-lg text-gray-200">
                {proofPoints.map((item) => <li key={item}>- {item}</li>)}
              </ul>
            </div>
          </div>

          <div className="mt-10 flex justify-center">
            <button
              onClick={() => handleCTAClick(organisatieUrl, 'problem')}
              className="rounded-2xl bg-[#713d8d] px-8 py-4 text-lg font-black text-white transition hover:translate-x-1 hover:translate-y-1"
              style={{ boxShadow: '8px 8px 0 #3f7f65' }}
            >
              {t('company_marketing.cta.start_org_arrow')}
            </button>
          </div>
        </div>
      </section>

      {/* PIJLERS */}
      <section className="relative z-10 py-20 md:py-28">
        <div className="mx-auto max-w-5xl px-6">
          <div className="mb-14 text-center">
            <TornLabel className="-rotate-2" bgColor={DADA.green} textColor="#ffffff">{t('company_marketing.pillars_title')}</TornLabel>
            <p className="mt-6 text-xl leading-relaxed text-[#2a2f40] md:text-2xl">
              {t('company_marketing.pillars_body_1')}
              <br />
              <span className="font-bold text-[#151827]">{t('company_marketing.pillars_body_2')}</span>
            </p>
          </div>

          <div className="grid grid-cols-2 gap-6 md:grid-cols-4">
            {pillars.map((pillar, index) => (
              <div
                key={pillar}
                className={`rounded-[1.5rem] border-4 border-[#151827] bg-white p-6 text-center ${index % 2 === 0 ? '-rotate-1' : 'rotate-1'}`}
                style={{ boxShadow: index % 2 === 0 ? '8px 8px 0 #3f7f65' : '8px 8px 0 #713d8d' }}
              >
                <div className="mb-2 text-4xl font-black text-[#151827]">0{index + 1}</div>
                <div className="text-lg font-black uppercase text-[#2a2f40]">{pillar}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOE WERKT HET */}
      <section id="hoe-het-werkt" className="relative z-10 mx-auto max-w-7xl px-5 py-16 sm:px-8 md:py-24">
        <div className="mb-12 text-center">
          <TornLabel className="rotate-1" bgColor={DADA.purple} textColor="#ffffff">{t('company_marketing.difference_kicker')}</TornLabel>
          <h2 className="mt-6 text-[clamp(2rem,5vw,3.4rem)] font-black leading-[0.95] tracking-[-0.03em]">
            {t('company_marketing.difference_title')}
          </h2>
          <p className="mx-auto mt-6 max-w-3xl text-lg font-semibold leading-relaxed text-[#2a2f40]">
            {t('company_marketing.difference_body')}
          </p>
        </div>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {STEPS.map(([number, title, copy], index) => {
            const accent = index % 3 === 0 ? '#3f7f65' : index % 3 === 1 ? '#713d8d' : '#151827';
            return (
              <article
                key={number}
                className={`rounded-[2rem] border-4 border-[#151827] bg-white p-7 transition hover:-translate-y-1 ${index % 2 === 0 ? '' : '-rotate-1'}`}
                style={{ boxShadow: `10px 10px 0 ${accent}` }}
              >
                <div className="mb-5 text-5xl font-black text-[#713d8d]">{number}</div>
                <h3 className="mb-3 text-2xl font-black">{title}</h3>
                <p className="text-lg font-medium text-[#2a2f40]">{copy}</p>
              </article>
            );
          })}
        </div>
      </section>

      {/* TIMELINE 0 / 50 / 100 UUR */}
      <section className="relative z-10 bg-[#3f7f65] py-20 text-white md:py-28">
        <div className="mx-auto max-w-5xl px-6">
          <div className="mb-12 text-center">
            <h2 className="text-[clamp(2rem,6vw,3.8rem)] font-black leading-[0.95] tracking-[-0.03em]">
              {t('company_marketing.timeline_title')}
            </h2>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {[
              [`0 ${t('company_marketing.hours_unit')}`, t('company_marketing.timeline.start.title'), t('company_marketing.timeline.start.body')],
              [`50 ${t('company_marketing.hours_unit')}`, t('company_marketing.timeline.mid.title'), t('company_marketing.timeline.mid.body')],
              [`100 ${t('company_marketing.hours_unit')}`, t('company_marketing.timeline.end.title'), t('company_marketing.timeline.end.body')]
            ].map(([time, title, text], index) => (
              <div
                key={time}
                className={`rounded-[1.5rem] border-4 border-[#151827] bg-white p-6 text-[#151827] ${index === 1 ? 'rotate-1' : index === 2 ? '-rotate-1' : ''}`}
                style={{ boxShadow: '8px 8px 0 #151827' }}
              >
                <div className="text-4xl font-black text-[#713d8d]">{time}</div>
                <div className="mt-3 text-xl font-black uppercase">{title}</div>
                <p className="mt-3 text-base font-medium text-[#2a2f40]">{text}</p>
              </div>
            ))}
          </div>

          <p className="mt-12 text-center text-2xl font-black md:text-3xl">{t('company_marketing.quote')}</p>
        </div>
      </section>

      {/* WAAROM */}
      <section className="relative z-10 bg-[#151827] py-20 text-white md:py-28">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <h2 className="mb-12 text-[clamp(1.9rem,5vw,3.2rem)] font-black leading-[0.95] tracking-[-0.03em]">
            {t('company_marketing.reasons_title')}
          </h2>

          <ul className="space-y-4 text-left text-lg text-gray-200 md:text-xl">
            {reasons.map((reason, index) => (
              <li
                key={reason}
                className="flex items-start gap-4 rounded-[1.25rem] border-2 border-white/25 bg-white/5 p-5"
              >
                <span
                  className="flex h-10 w-10 flex-none items-center justify-center rounded-full text-base font-black text-white"
                  style={{ backgroundColor: index % 2 === 0 ? '#3f7f65' : '#713d8d' }}
                >
                  {index + 1}
                </span>
                <span className="font-semibold">{reason}</span>
              </li>
            ))}
          </ul>

          <div
            className="mt-12 -rotate-1 rounded-[1.75rem] border-4 border-white bg-white/10 p-8"
            style={{ boxShadow: '10px 10px 0 #3f7f65' }}
          >
            <p className="text-xl font-black text-[#8fd6b6] md:text-2xl">{t('company_marketing.reasons_outro')}</p>
          </div>
        </div>
      </section>

      {/* INVESTERING */}
      <section id="investering" className="relative z-10 py-20 md:py-28">
        <div className="mx-auto max-w-5xl px-6">
          <div className="mb-14 text-center">
            <TornLabel className="-rotate-2" bgColor={DADA.green} textColor="#ffffff">{t('company_marketing.simple_start_kicker')}</TornLabel>
            <h2 className="mt-6 text-[clamp(2rem,5vw,3.4rem)] font-black leading-[0.95] tracking-[-0.03em]">
              {t('company_marketing.simple_start_title')}
            </h2>
            <p className="mt-4 text-lg font-medium text-[#2a2f40]">{t('company_marketing.simple_start_body')}</p>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            <div
              className="rotate-1 rounded-[1.75rem] border-4 border-[#151827] bg-[#3f7f65] p-7 text-white"
              style={{ boxShadow: '10px 10px 0 #151827' }}
            >
              <div className="text-xs font-black uppercase tracking-[0.2em] text-white/80">{t('company_marketing.investment.pilot_label')}</div>
              <div className="mt-3 text-3xl font-black uppercase">{t('company_marketing.investment.pilot_value')}</div>
              <p className="mt-4 text-base font-semibold text-white/90">{t('company_marketing.investment.pilot_body')}</p>
            </div>
            <div
              className="-rotate-1 rounded-[1.75rem] border-4 border-[#151827] bg-white p-7"
              style={{ boxShadow: '10px 10px 0 #713d8d' }}
            >
              <div className="text-xs font-black uppercase tracking-[0.2em] text-[#151827]/60">{t('company_marketing.investment.timeline_label')}</div>
              <div className="mt-3 text-3xl font-black uppercase">{t('company_marketing.investment.timeline_value')}</div>
              <p className="mt-4 text-base font-semibold text-[#2a2f40]">{t('company_marketing.investment.timeline_body')}</p>
            </div>
            <div
              className="rotate-1 rounded-[1.75rem] border-4 border-[#151827] bg-[#151827] p-7 text-white"
              style={{ boxShadow: '10px 10px 0 #3f7f65' }}
            >
              <div className="text-xs font-black uppercase tracking-[0.2em] text-white/70">{t('company_marketing.investment.cost_label')}</div>
              <div className="mt-3 text-2xl font-black uppercase leading-tight">{t('company_marketing.investment.cost_value')}</div>
              <p className="mt-4 text-base font-semibold text-white/85">{t('company_marketing.investment.cost_body')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA + AFSPRAAK */}
      <section className="relative z-10 mx-auto max-w-7xl px-5 py-16 sm:px-8 md:py-24">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1.1fr_1fr]">
          <div
            className="-rotate-1 rounded-[2rem] border-4 border-[#151827] bg-[#713d8d] p-8 text-white md:p-12"
            style={{ boxShadow: '14px 14px 0 #151827' }}
          >
            <h2 className="text-[clamp(2rem,6vw,3.8rem)] font-black uppercase leading-[0.95] tracking-[-0.03em]">
              {t('company_marketing.final_cta.title')}
            </h2>
            <p className="mt-6 text-xl font-semibold leading-relaxed">
              {t('company_marketing.final_cta.body')}
            </p>
            <div className="mt-8 flex flex-col gap-4 sm:flex-row">
              <button
                onClick={() => handleCTAClick(organisatieUrl, 'final-cta')}
                className="rounded-2xl bg-white px-7 py-4 text-base font-black text-[#151827] transition hover:-translate-y-0.5"
                style={{ boxShadow: '6px 6px 0 #151827' }}
              >
                {t('company_marketing.cta.start_org_arrow')}
              </button>
              <button
                onClick={scrollToAppointmentForm}
                className="rounded-2xl border-4 border-white bg-transparent px-7 py-4 text-base font-black text-white transition hover:bg-white hover:text-[#151827]"
              >
                {t('company_marketing.cta.make_appointment')}
              </button>
            </div>
          </div>

          <section
            id="afspraak-formulier"
            className="rotate-1 rounded-[2rem] border-4 border-[#151827] bg-white p-8"
            style={{ boxShadow: '14px 14px 0 #3f7f65' }}
          >
            <h2 className="text-[clamp(1.6rem,4vw,2.6rem)] font-black uppercase leading-tight tracking-[-0.02em]">{t('company_marketing.form.title')}</h2>
            <p className="mt-3 text-lg font-medium text-[#2a2f40]">{t('company_marketing.form.subtitle')}</p>

            {appointmentSent ? (
              <div className="mt-8 rounded-2xl border-4 border-[#151827] p-5 text-lg font-black" style={{ backgroundColor: '#edf5f1', color: DADA.green }}>
                {t('company_marketing.form.success')}
              </div>
            ) : (
              <form onSubmit={handleAppointmentSubmit} className="mt-8 space-y-4">
                {appointmentError && (
                  <div className="rounded-2xl border-4 border-[#151827] bg-red-50 px-4 py-3 text-sm font-bold text-red-700">
                    {appointmentError}
                  </div>
                )}

                <input
                  type="text"
                  placeholder={t('company_marketing.form.name_placeholder')}
                  value={appointmentForm.name}
                  onChange={(event) => setAppointmentForm({ ...appointmentForm, name: event.target.value })}
                  className="min-h-[52px] w-full rounded-2xl border-4 border-[#151827] px-4 py-3 text-base font-semibold outline-none focus:ring-4 focus:ring-[#713d8d]/30"
                  required
                />
                <input
                  type="email"
                  placeholder={t('company_marketing.form.email_placeholder')}
                  value={appointmentForm.email}
                  onChange={(event) => setAppointmentForm({ ...appointmentForm, email: event.target.value })}
                  className="min-h-[52px] w-full rounded-2xl border-4 border-[#151827] px-4 py-3 text-base font-semibold outline-none focus:ring-4 focus:ring-[#713d8d]/30"
                  required
                />
                <input
                  type="text"
                  placeholder={t('company_marketing.form.company_placeholder')}
                  value={appointmentForm.company_name}
                  onChange={(event) => setAppointmentForm({ ...appointmentForm, company_name: event.target.value })}
                  className="min-h-[52px] w-full rounded-2xl border-4 border-[#151827] px-4 py-3 text-base font-semibold outline-none focus:ring-4 focus:ring-[#713d8d]/30"
                  required
                />
                <input
                  type="text"
                  placeholder={t('company_marketing.form.phone_placeholder')}
                  value={appointmentForm.phone}
                  onChange={(event) => setAppointmentForm({ ...appointmentForm, phone: event.target.value })}
                  className="min-h-[52px] w-full rounded-2xl border-4 border-[#151827] px-4 py-3 text-base font-semibold outline-none focus:ring-4 focus:ring-[#713d8d]/30"
                />
                <textarea
                  rows={5}
                  placeholder={t('company_marketing.form.message_placeholder')}
                  value={appointmentForm.message}
                  onChange={(event) => setAppointmentForm({ ...appointmentForm, message: event.target.value })}
                  className="w-full rounded-2xl border-4 border-[#151827] px-4 py-3 text-base font-semibold outline-none focus:ring-4 focus:ring-[#713d8d]/30"
                />
                <button
                  type="submit"
                  disabled={appointmentBusy}
                  className="min-h-[56px] w-full rounded-2xl border-4 border-[#151827] bg-[#713d8d] px-6 py-3 text-base font-black uppercase text-white transition hover:-translate-y-0.5 disabled:opacity-60"
                  style={{ boxShadow: '6px 6px 0 #151827' }}
                >
                  {appointmentBusy ? t('auth.sending') : t('company_marketing.form.submit')}
                </button>
              </form>
            )}
          </section>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="relative z-10 bg-[#151827] py-12 text-white">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="grid grid-cols-1 gap-8 md:grid-cols-[1fr_2fr]">
            <div>
              <div className="text-2xl font-black uppercase">werepresenttalent.nl</div>
              <p className="mt-3 text-sm font-medium text-white/65">
                {t('company_marketing.footer.brand_copy', { year: new Date().getFullYear() })}
              </p>
            </div>
            <nav className="flex flex-wrap justify-start gap-x-6 gap-y-3 text-sm font-bold text-white/80 md:justify-end">
              <button onClick={() => onNavigate(kandidaatUrl)} className="transition hover:text-white">{t('company_marketing.footer.betaaldsolliciteren')}</button>
              <button onClick={() => onNavigate(werepresentUrl)} className="transition hover:text-white">WeRepresent</button>
              <a href="mailto:info@betaaldsolliciteren.nl" className="transition hover:text-white">{t('company_marketing.footer.contact')}</a>
              <button onClick={() => onNavigate(privacyUrl)} className="transition hover:text-white">{t('company_marketing.footer.privacy')}</button>
              <button onClick={() => onNavigate(voorwaardenUrl)} className="transition hover:text-white">{t('company_marketing.footer.terms')}</button>
              <button onClick={() => onNavigate(aiPolicyUrl)} className="transition hover:text-white">AI Policy</button>
              <button onClick={() => onNavigate(cookieUrl)} className="transition hover:text-white">{t('company_marketing.footer.cookies')}</button>
            </nav>
          </div>
          <div className="mt-8 border-t border-white/15 pt-6 text-center text-sm text-white/50">
            {t('company_marketing.footer.bottom_line')}
          </div>
        </div>
      </footer>
    </main>
  );
}
